import React from 'react';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { PublicNavigation } from '../PublicNavigation';
import { PublicFooter } from '../PublicFooter';

interface PageLayoutProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: Array<{ label: string; href?: string }>;
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
}

export const PageLayout: React.FC<PageLayoutProps> = ({
  title,
  subtitle,
  breadcrumbs,
  children,
  maxWidth = 'lg',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Public Navigation */}
      <PublicNavigation />
      
      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, py: { xs: 4, md: 6 } }}>
        <Container maxWidth={maxWidth}>
          {/* Breadcrumbs */}
          {breadcrumbs && breadcrumbs.length > 0 && (
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              sx={{ mb: 3 }}
            >
              <Link
                href="/"
                sx={{
                  color: theme.palette.text.secondary,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Home
              </Link>
              {breadcrumbs.map((crumb, index) => (
                <Box key={index}>
                  {crumb.href ? (
                    <Link
                      href={crumb.href}
                      sx={{
                        color: theme.palette.text.secondary,
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <Typography
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      {crumb.label}
                    </Typography>
                  )}
                </Box>
              ))}
            </Breadcrumbs>
          )}

          {/* Page Header */}
          <Box sx={{ mb: { xs: 4, md: 6 } }}>
            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              component="h1"
              sx={{
                fontWeight: 'bold',
                color: theme.palette.primary.main,
                mb: subtitle ? 2 : 0,
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.4,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {/* Page Content */}
          {children}
        </Container>
      </Box>
      
      {/* Footer */}
      <PublicFooter />
    </Box>
  );
};
